import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Plus, X, Trash2, Edit, Save, Users, Settings, AlertCircle, UserPlus, Search } from 'lucide-react';
import './TeamsManagement.css';

const TeamsManagement = () => {
  const { authState } = useOktaAuth();
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { type, id, field }
  const [editingValue, setEditingValue] = useState('');
  const [teamFormData, setTeamFormData] = useState({
    title: '',
    manager: '',
    director: ''
  });
  const [roleFormData, setRoleFormData] = useState({
    title: '',
    teamId: ''
  });
  const [roleResources, setRoleResources] = useState({});
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'resources', 'employees'
  const [roleEmployees, setRoleEmployees] = useState({});
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [functionalRoleResources, setFunctionalRoleResources] = useState([]);
  const [selectedTeamForRole, setSelectedTeamForRole] = useState(null);
  const [expandedResources, setExpandedResources] = useState(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTeams(),
        fetchRoles(),
        fetchAllRoleResources(),
        fetchEmployees(),
        fetchFunctionalRoles(),
        fetchFunctionalRoleResources()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFunctionalRoles = async () => {
    try {
      const response = await fetch('/api/subroles');
      if (response.ok) {
        const data = await response.json();
        setFunctionalRoles(data);
      }
    } catch (error) {
      console.error('Error fetching functional roles:', error);
      setFunctionalRoles([]);
    }
  };

  const fetchFunctionalRoleResources = async () => {
    try {
      const response = await fetch('/api/subrole-resources');
      if (response.ok) {
        const data = await response.json();
        setFunctionalRoleResources(data);
      }
    } catch (error) {
      console.error('Error fetching functional role resources:', error);
      setFunctionalRoleResources([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees?limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
        // Group employees by role for quick lookup
        const employeesByRole = {};
        data.forEach(employee => {
          const roleTitle = employee.role;
          if (roleTitle) {
            if (!employeesByRole[roleTitle]) {
              employeesByRole[roleTitle] = [];
            }
            employeesByRole[roleTitle].push(employee);
          }
        });
        setRoleEmployees(employeesByRole);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllRoleResources = async () => {
    try {
      const response = await fetch('/api/rbac');
      if (response.ok) {
        const allRbacEntries = await response.json();
        // Group approved resources by role ID
        const resourcesByRole = {};
        allRbacEntries.forEach(entry => {
          if (entry.Status === 'Approved') {
            if (!resourcesByRole[entry.RoleID]) {
              resourcesByRole[entry.RoleID] = [];
            }
            resourcesByRole[entry.RoleID].push(entry);
          }
        });
        setRoleResources(resourcesByRole);
      }
    } catch (error) {
      console.error('Error fetching role resources:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams');
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/roles');
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };


  const handleAddTeam = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/teams', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...teamFormData,
          createdBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setTeams(prev => [...prev, result.team]);
        setTeamFormData({ title: '', manager: '', director: '' });
        setShowAddTeamModal(false);
        
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'success-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <span class="notification-icon">✓</span>
            <span class="notification-message">Team "${teamFormData.title}" created successfully!</span>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.classList.add('fade-out');
          setTimeout(() => document.body.removeChild(notification), 300);
        }, 3000);
      } else {
        const error = await response.json();
        alert('Error adding team: ' + error.error);
      }
    } catch (error) {
      console.error('Error adding team:', error);
      alert('Error adding team. Please try again.');
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...roleFormData,
          createdBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setRoles(prev => [...prev, result.role]);
        setRoleFormData({ title: '', teamId: '' });
        setShowAddRoleModal(false);
        setSelectedTeamForRole(null);
        alert('Role added successfully!');
      } else {
        const error = await response.json();
        alert('Error adding role: ' + error.error);
      }
    } catch (error) {
      console.error('Error adding role:', error);
      alert('Error adding role');
    }
  };

  const handleDeleteTeam = (teamId) => {
    const team = teams.find(t => t.TeamID === teamId);
    setTeamToDelete(team);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      const response = await fetch(`/api/teams/${teamToDelete.TeamID}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setTeams(prev => prev.filter(team => team.TeamID !== teamToDelete.TeamID));
        setRoles(prev => prev.filter(role => role.TeamID !== teamToDelete.TeamID));
        
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'success-notification delete-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <span class="notification-icon">🗑️</span>
            <span class="notification-message">Team "${teamToDelete.Title}" has been deleted</span>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.classList.add('fade-out');
          setTimeout(() => document.body.removeChild(notification), 300);
        }, 3000);
      } else {
        alert('Error deleting team. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('Error deleting team. Please check your connection and try again.');
    } finally {
      setShowDeleteConfirmModal(false);
      setTeamToDelete(null);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }

    try {
      const response = await fetch(`/api/roles/${roleId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setRoles(prev => prev.filter(role => role.RoleID !== roleId));
        alert('Role deleted successfully!');
      } else {
        alert('Error deleting role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Error deleting role');
    }
  };

  const openAddRoleModal = (teamId = null) => {
    setSelectedTeamForRole(teamId);
    setRoleFormData({ title: '', teamId: teamId || '' });
    setShowAddRoleModal(true);
  };

  const getRolesForTeam = (teamId) => {
    return roles.filter(role => role.TeamID === teamId);
  };

  // Inline editing functions
  const startEditing = (type, id, field, currentValue) => {
    setEditingCell({ type, id, field });
    setEditingValue(currentValue);
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;

    try {
      const { type, id, field } = editingCell;
      const endpoint = type === 'team' ? 'teams' : 'roles';
      
      const response = await fetch(`/api/${endpoint}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          [field]: editingValue,
          modifiedBy: authState?.idToken?.claims?.name || 'System User'
        }),
      });

      if (response.ok) {
        // Update local state
        if (type === 'team') {
          setTeams(prev => prev.map(team => 
            team.TeamID === id 
              ? { ...team, [field === 'title' ? 'Title' : field]: editingValue }
              : team
          ));
        } else {
          setRoles(prev => prev.map(role => 
            role.RoleID === id 
              ? { ...role, [field === 'title' ? 'Title' : field]: editingValue }
              : role
          ));
        }
        
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'success-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <span class="notification-icon">✓</span>
            <span class="notification-message">Updated successfully!</span>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.classList.add('fade-out');
          setTimeout(() => document.body.removeChild(notification), 300);
        }, 2000);
        
        cancelEditing();
      } else {
        alert('Error updating record');
      }
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('Error saving changes');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const toggleResourcesExpand = (roleId) => {
    setExpandedResources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  };

  const toggleEmployeesExpand = (roleId) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="teams-management">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div className="loading-spinner"></div>
          <div>Loading teams data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="teams-management">
      <div className="teams-header">
        <div>
          <h2>Teams & Roles Management</h2>
          <p>Manage teams and their associated roles</p>
        </div>
        <div className="header-actions">
          <button 
            className="add-team-btn"
            onClick={() => setShowAddTeamModal(true)}
          >
            <Plus size={16} />
            Add Team
          </button>
          <button 
            className="add-role-btn"
            onClick={() => openAddRoleModal()}
          >
            <UserPlus size={16} />
            Add Role
          </button>
        </div>
      </div>

      {/* Search Bar and View Filter Tabs on Same Line */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <div className="search-container" style={{ 
          flex: '0 1 450px', 
          minWidth: '300px', 
          maxWidth: '500px',
          marginBottom: '0' 
        }}>
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search teams, roles, managers..."
            className="search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="clear-search-btn"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="view-filter-tabs" style={{ marginBottom: '0', flexShrink: 0 }}>
          <button 
            className={`filter-tab ${activeView === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveView('resources')}
          >
            Approved Resources
          </button>
          <button 
            className={`filter-tab ${activeView === 'employees' ? 'active' : ''}`}
            onClick={() => setActiveView('employees')}
          >
            Employees
          </button>
        </div>
      </div>

      {/* Add Team Modal */}
      {showAddTeamModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Team</h3>
              <button className="close-btn" onClick={() => setShowAddTeamModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddTeam} className="modal-form">
              <div className="form-group">
                <label htmlFor="teamTitle">Team Name *</label>
                <input
                  type="text"
                  id="teamTitle"
                  value={teamFormData.title}
                  onChange={e => setTeamFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Engineering, Marketing, Sales"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="teamManager">Manager</label>
                <input
                  type="text"
                  id="teamManager"
                  value={teamFormData.manager}
                  onChange={e => setTeamFormData(prev => ({ ...prev, manager: e.target.value }))}
                  placeholder="Manager Name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="teamDirector">Director</label>
                <input
                  type="text"
                  id="teamDirector"
                  value={teamFormData.director}
                  onChange={e => setTeamFormData(prev => ({ ...prev, director: e.target.value }))}
                  placeholder="Director Name"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddTeamModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Add Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Role Modal */}
      {showAddRoleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Role</h3>
              <button className="close-btn" onClick={() => setShowAddRoleModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddRole} className="modal-form">
              <div className="form-group">
                <label htmlFor="roleTeam">Team *</label>
                <select
                  id="roleTeam"
                  value={roleFormData.teamId}
                  onChange={e => setRoleFormData(prev => ({ ...prev, teamId: e.target.value }))}
                  required
                >
                  <option value="">Select Team</option>
                  {teams.map(team => (
                    <option key={team.TeamID} value={team.TeamID}>{team.Title}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="roleTitle">Role Name *</label>
                <input
                  type="text"
                  id="roleTitle"
                  value={roleFormData.title}
                  onChange={e => setRoleFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Senior Developer, Marketing Manager"
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddRoleModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Add Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && teamToDelete && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <div className="delete-icon-header">
                <div className="delete-icon-circle">
                  <Trash2 size={24} />
                </div>
                <h3>Delete Team</h3>
              </div>
              <button className="close-btn" onClick={() => setShowDeleteConfirmModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-form">
              <div className="delete-content">
                <p className="delete-team-name">
                  Are you sure you want to delete <strong>"{teamToDelete.Title}"</strong>?
                </p>
                <div className="delete-warning">
                  <AlertCircle size={16} />
                  <span>This action will permanently delete the team and all associated roles. This cannot be undone.</span>
                </div>
                {getRolesForTeam(teamToDelete.TeamID).length > 0 && (
                  <div className="roles-impact">
                    <p><strong>{getRolesForTeam(teamToDelete.TeamID).length} role(s) will also be deleted:</strong></p>
                    <ul>
                      {getRolesForTeam(teamToDelete.TeamID).map(role => (
                        <li key={role.RoleID}>{role.Title}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowDeleteConfirmModal(false)} 
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={confirmDeleteTeam} 
                  className="btn-delete"
                >
                  <Trash2 size={16} />
                  Delete Team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Excel-like Grid View */}
      <div className="excel-grid-container">
        {teams.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} className="empty-icon" />
            <h3>No teams found</h3>
            <p>Click "Add Team" to create your first team.</p>
          </div>
        ) : (
          <div className="excel-table-wrapper">
            <table className="excel-table">
              <thead>
                <tr>
                  <th className="col-team">Team Name</th>
                  <th className="col-manager">Manager</th>
                  <th className="col-director">Director</th>
                  <th className="col-role">Role Name</th>
                  <th className="col-resources">Resources</th>
                  <th className="col-employees">Employees</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {teams.filter(team => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase();
                  const teamRoles = getRolesForTeam(team.TeamID);
                  
                  return (
                    team.Title.toLowerCase().includes(query) ||
                    (team.Manager && team.Manager.toLowerCase().includes(query)) ||
                    (team.Director && team.Director.toLowerCase().includes(query)) ||
                    teamRoles.some(role => role.Title.toLowerCase().includes(query))
                  );
                }).map(team => {
                  const teamRoles = getRolesForTeam(team.TeamID);
                  
                  if (teamRoles.length === 0) {
                    return (
                      <tr key={`team-${team.TeamID}`} className="team-row no-roles">
                        <td className="team-cell">
                          {editingCell?.type === 'team' && editingCell?.id === team.TeamID && editingCell?.field === 'title' ? (
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={saveEdit}
                              autoFocus
                              className="inline-edit-input"
                            />
                          ) : (
                            <div
                              className="editable-cell"
                              onClick={() => startEditing('team', team.TeamID, 'title', team.Title)}
                            >
                              <span className="cell-content">{team.Title}</span>
                              <Edit size={12} className="edit-icon" />
                            </div>
                          )}
                        </td>
                        <td className="manager-cell">
                          {editingCell?.type === 'team' && editingCell?.id === team.TeamID && editingCell?.field === 'manager' ? (
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={saveEdit}
                              autoFocus
                              className="inline-edit-input"
                            />
                          ) : (
                            <div
                              className="editable-cell"
                              onClick={() => startEditing('team', team.TeamID, 'manager', team.Manager || '')}
                            >
                              <span className="cell-content">{team.Manager || 'Not set'}</span>
                              <Edit size={12} className="edit-icon" />
                            </div>
                          )}
                        </td>
                        <td className="director-cell">
                          {editingCell?.type === 'team' && editingCell?.id === team.TeamID && editingCell?.field === 'director' ? (
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={saveEdit}
                              autoFocus
                              className="inline-edit-input"
                            />
                          ) : (
                            <div
                              className="editable-cell"
                              onClick={() => startEditing('team', team.TeamID, 'director', team.Director || '')}
                            >
                              <span className="cell-content">{team.Director || 'Not set'}</span>
                              <Edit size={12} className="edit-icon" />
                            </div>
                          )}
                        </td>
                        <td className="role-cell empty">
                          <span className="no-roles-text">No roles</span>
                        </td>
                        <td className="resources-cell empty">—</td>
                        <td className="employees-cell empty">—</td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="action-btn add-role-btn"
                              onClick={() => openAddRoleModal(team.TeamID)}
                              title="Add role to this team"
                            >
                              <Plus size={12} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteTeam(team.TeamID)}
                              title="Delete team"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return teamRoles.map((role, roleIndex) => {
                    const roleDirectResources = roleResources[role.RoleID] || [];
                    const roleFunctionalRoles = functionalRoles.filter(fr => fr.parentRoleId === role.RoleID);
                    const roleFunctionalRoleResources = roleFunctionalRoles.reduce((frAcc, functionalRole) => {
                      const frResources = functionalRoleResources.filter(frr => frr.subroleId === functionalRole.id);
                      return frAcc.concat(frResources.map(frResource => ({
                        RBACID: `fr-${frResource.id}`,
                        Resource: frResource.resourceName,
                        isFunctionalRole: true,
                        functionalRoleName: functionalRole.name,
                        TargetSystem: frResource.TargetSystem,
                        DataRisk: frResource.DataRisk
                      })));
                    }, []);
                    
                    const roleApprovedResources = [...roleDirectResources, ...roleFunctionalRoleResources];
                    const roleEmployeesList = roleEmployees[role.Title] || [];

                    return (
                      <tr key={`role-${role.RoleID}`} className={`role-row ${roleIndex === 0 ? 'first-role' : ''}`}>
                        {roleIndex === 0 ? (
                          <>
                            <td className="team-cell" rowSpan={teamRoles.length}>
                              {editingCell?.type === 'team' && editingCell?.id === team.TeamID && editingCell?.field === 'title' ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onKeyDown={handleKeyPress}
                                  onBlur={saveEdit}
                                  autoFocus
                                  className="inline-edit-input"
                                />
                              ) : (
                                <div
                                  className="editable-cell"
                                  onClick={() => startEditing('team', team.TeamID, 'title', team.Title)}
                                >
                                  <span className="cell-content team-name">{team.Title}</span>
                                  <Edit size={12} className="edit-icon" />
                                </div>
                              )}
                            </td>
                            <td className="manager-cell" rowSpan={teamRoles.length}>
                              {editingCell?.type === 'team' && editingCell?.id === team.TeamID && editingCell?.field === 'manager' ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onKeyDown={handleKeyPress}
                                  onBlur={saveEdit}
                                  autoFocus
                                  className="inline-edit-input"
                                />
                              ) : (
                                <div
                                  className="editable-cell"
                                  onClick={() => startEditing('team', team.TeamID, 'manager', team.Manager || '')}
                                >
                                  <span className="cell-content">{team.Manager || 'Not set'}</span>
                                  <Edit size={12} className="edit-icon" />
                                </div>
                              )}
                            </td>
                            <td className="director-cell" rowSpan={teamRoles.length}>
                              {editingCell?.type === 'team' && editingCell?.id === team.TeamID && editingCell?.field === 'director' ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onKeyDown={handleKeyPress}
                                  onBlur={saveEdit}
                                  autoFocus
                                  className="inline-edit-input"
                                />
                              ) : (
                                <div
                                  className="editable-cell"
                                  onClick={() => startEditing('team', team.TeamID, 'director', team.Director || '')}
                                >
                                  <span className="cell-content">{team.Director || 'Not set'}</span>
                                  <Edit size={12} className="edit-icon" />
                                </div>
                              )}
                            </td>
                          </>
                        ) : null}
                        
                        <td className="role-cell">
                          {editingCell?.type === 'role' && editingCell?.id === role.RoleID && editingCell?.field === 'title' ? (
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={saveEdit}
                              autoFocus
                              className="inline-edit-input"
                            />
                          ) : (
                            <div
                              className="editable-cell"
                              onClick={() => startEditing('role', role.RoleID, 'title', role.Title)}
                            >
                              <span className="cell-content role-name">{role.Title}</span>
                              <Edit size={12} className="edit-icon" />
                            </div>
                          )}
                        </td>
                        
                        <td className="resources-cell">
                          <div className="count-badge resources-badge">
                            <span className="count">{roleApprovedResources.length}</span>
                            <span className="label">resources</span>
                          </div>
                          {roleApprovedResources.length > 0 && (
                            <div className="resource-preview">
                              {(expandedResources.has(role.RoleID) 
                                ? roleApprovedResources 
                                : roleApprovedResources.slice(0, 3)
                              ).map((resource, idx) => (
                                <span key={idx} className="resource-tag">
                                  {resource.Resource}
                                  {resource.isFunctionalRole && <span className="functional-badge">F</span>}
                                </span>
                              ))}
                              {roleApprovedResources.length > 3 && (
                                <button 
                                  className="expand-toggle-btn"
                                  onClick={() => toggleResourcesExpand(role.RoleID)}
                                  title={expandedResources.has(role.RoleID) ? "Show less" : "Show all"}
                                >
                                  {expandedResources.has(role.RoleID) 
                                    ? 'Show less' 
                                    : `+${roleApprovedResources.length - 3} more`
                                  }
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        
                        <td className="employees-cell">
                          <div className="count-badge employees-badge">
                            <span className="count">{roleEmployeesList.length}</span>
                            <span className="label">employees</span>
                          </div>
                          {roleEmployeesList.length > 0 && (
                            <div className="employee-preview">
                              {(expandedEmployees.has(role.RoleID)
                                ? roleEmployeesList
                                : roleEmployeesList.slice(0, 2)
                              ).map((employee, idx) => (
                                <span key={idx} className="employee-tag">
                                  {employee.name}
                                </span>
                              ))}
                              {roleEmployeesList.length > 2 && (
                                <button 
                                  className="expand-toggle-btn"
                                  onClick={() => toggleEmployeesExpand(role.RoleID)}
                                  title={expandedEmployees.has(role.RoleID) ? "Show less" : "Show all"}
                                >
                                  {expandedEmployees.has(role.RoleID)
                                    ? 'Show less'
                                    : `+${roleEmployeesList.length - 2} more`
                                  }
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteRole(role.RoleID)}
                              title="Delete role"
                            >
                              <Trash2 size={12} />
                            </button>
                            {roleIndex === 0 && (
                              <button
                                className="action-btn delete-team-btn"
                                onClick={() => handleDeleteTeam(team.TeamID)}
                                title="Delete entire team"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsManagement;
